import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";

const SessionContext = createContext(null);
const STORAGE_KEY = "angadya.session.v1";
const FOCUS_KEY = "angadya.focus.v1";

export const SessionProvider = ({ children }) => {
  const [bootstrap, setBootstrap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [focusBranchId, setFocusBranchIdState] = useState(() => {
    try {
      return sessionStorage.getItem(FOCUS_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let mounted = true;
    api
      .get("/session/bootstrap")
      .then((r) => {
        if (mounted) setBootstrap(r.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  }, [session]);

  useEffect(() => {
    try {
      if (focusBranchId) sessionStorage.setItem(FOCUS_KEY, focusBranchId);
      else sessionStorage.removeItem(FOCUS_KEY);
    } catch {}
  }, [focusBranchId]);

  const login = (payload) => {
    setSession(payload);
    setFocusBranchIdState(null); // reset focus on role change
  };
  const logout = () => {
    setSession(null);
    setFocusBranchIdState(null);
  };
  const setFocus = (branchId) => setFocusBranchIdState(branchId);
  const clearFocus = () => setFocusBranchIdState(null);

  const roleMeta = useMemo(() => {
    if (!session || !bootstrap) return null;
    return bootstrap.roles.find((r) => r.id === session.role);
  }, [session, bootstrap]);

  const allowedModules = useMemo(() => {
    if (!session || !bootstrap) return [];
    return bootstrap.role_modules[session.role] || [];
  }, [session, bootstrap]);

  const focusBranch = useMemo(() => {
    if (!focusBranchId || !bootstrap) return null;
    return bootstrap.branches.find((b) => b.id === focusBranchId) || null;
  }, [focusBranchId, bootstrap]);

  const value = {
    bootstrap, loading, session, login, logout, roleMeta, allowedModules,
    focusBranchId, focusBranch, setFocus, clearFocus,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
};
